/**
 * Route-level error boundary — catches errors thrown by a specific routed
 * page without destroying the surrounding Layout (Sidebar / Header /
 * MobileNav). This is the difference between "one broken page" and "the
 * whole app is broken". Reuses the same presentation as the global
 * ErrorBoundary but scoped to the main content area.
 *
 * Used inside Layout around <Outlet />.
 */
import React from 'react';
import { AlertTriangle, RefreshCw, ShieldAlert, WifiOff, ArrowLeft } from 'lucide-react';

const isChunkLoadError = (err) => {
  if (!err) return false;
  const msg = String(err.message || err);
  return (
    err?.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/.test(msg) ||
    /Failed to fetch dynamically imported module/.test(msg)
  );
};
const isForbidden = (err) => err?.status === 403 || err?.status === 401;
const isNetworkError = (err) => {
  if (!err) return false;
  if (err.code === 'network') return true;
  return err?.name === 'TypeError' && /Failed to fetch|NetworkError|Load failed/i.test(String(err.message || ''));
};

class RouteErrorBoundary extends React.Component {
  state = { hasError: false, error: null, key: 0 };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      const KEY = 'pie:route-err-reloaded-at';
      try {
        const last = parseInt(sessionStorage.getItem(KEY) || '0', 10);
        if (Date.now() - last > 30_000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          if ('caches' in window) {
            caches.keys().then((names) =>
              Promise.all(names.map((n) => caches.delete(n)))
            ).finally(() => window.location.reload());
          } else {
            window.location.reload();
          }
          return;
        }
      } catch (_) {}
    }
    try { console.error('[RouteErrorBoundary]', error); } catch (_) {}
  }

  // Reset when the route changes (via a key passed in as a prop)
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  retry = () => this.setState((s) => ({ hasError: false, error: null, key: s.key + 1 }));

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.error;

    if (isChunkLoadError(err)) {
      return (
        <div className="flex items-center justify-center py-16" data-testid="route-err-chunk">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-[#DC2626] rounded-full animate-spin mx-auto mb-3" />
            <p className="text-slate-600 text-sm">Updating to the latest version…</p>
          </div>
        </div>
      );
    }

    if (isForbidden(err)) {
      const role = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}')?.role || 'guest'; }
        catch { return 'guest'; }
      })();
      return (
        <div className="max-w-xl mx-auto my-8" data-testid="route-err-forbidden">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
            <ShieldAlert className="w-10 h-10 text-amber-600 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-amber-900">You don&apos;t have access to this page</h2>
            <p className="text-sm text-amber-800 mt-1">
              Your role (<strong>{role}</strong>) can&apos;t open this page. If this is a mistake, ask an admin to grant access.
            </p>
            <button
              onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = '/')}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
            >
              <ArrowLeft size={16} /> Back
            </button>
          </div>
        </div>
      );
    }

    if (isNetworkError(err)) {
      return (
        <div className="max-w-xl mx-auto my-8" data-testid="route-err-network">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
            <WifiOff className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-900">Connection issue</h2>
            <p className="text-sm text-slate-600 mt-1">
              We couldn&apos;t reach the PIE server. Check your internet and retry.
            </p>
            <button
              onClick={this.retry}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#DC2626] text-white rounded-lg hover:bg-[#B91C1C]"
            >
              <RefreshCw size={16} /> Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-xl mx-auto my-8" data-testid="route-err-generic">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-10 h-10 text-red-600 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-900">Something went wrong on this page</h2>
          <p className="text-sm text-red-800 mt-1">
            You can still use the rest of the app. Retry, go back, or reload.
          </p>
          <div className="flex gap-2 justify-center mt-4">
            <button
              onClick={this.retry}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#DC2626] text-white rounded-lg hover:bg-[#B91C1C]"
            >
              <RefreshCw size={16} /> Retry
            </button>
            <button
              onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = '/')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
            >
              <ArrowLeft size={16} /> Back
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && err && (
            <details className="mt-4 text-left">
              <summary className="text-xs text-red-600 cursor-pointer">Error details</summary>
              <pre className="mt-2 p-2 bg-red-100 rounded text-[11px] overflow-auto max-h-32">{err.toString()}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
